import {useAuth} from '@apps-common/auth-react';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {Box, Chip, Tooltip, Typography, styled} from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {getImageThumbnailURL, getMosaicThumbnailURL} from '../../api.js';
import {getLayerTypeAndId, isMosaicLayer} from '../../util/collection.js';
import {Avatar} from '../Avatar.js';
import CopyButton from '../CopyButton.jsx';
import IconButton from '../IconButton.js';
import {ImageryLink} from './ImageryLink.jsx';

const ObservationItemParent = styled(Box)(({theme, selected}) => ({
  backgroundColor: selected ? theme.vars.palette.primary.light : undefined,
  borderBottom: `solid 1px ${theme.vars.palette.divider}`,
  display: 'flex',
  cursor: 'pointer',
}));

ObservationItemParent.propTypes = {
  theme: PropTypes.object,
  selected: PropTypes.bool,
};

const BigChip = styled(Chip)({
  fontSize: 16,
});

const ListText = styled(Typography)({
  maxHeight: 30,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

ListText.defaultProps = {
  ...ListText.defaultProps,
  variant: 'body1',
  component: 'span',
};

const getFormattedDate = (item, isMosaic) => {
  const d = dayjs(item.observed).utc();
  let formatString = isMosaic ? 'MMMM YYYY' : 'DD MMMM YYYY, HH:mm:ss.SSS';
  // this is a weekly basemap or an adc basemap
  // unfortunately adc daily basemaps do not have "daily" in the name
  // example name adc_prod_sr_2023_08_30_v0_mosaic
  if (
    isMosaic &&
    (item.layer.indexOf('weekly') >= 0 || item.layer.indexOf('adc_') >= 0)
  ) {
    formatString = 'DD MMMM YYYY';
  }

  return d.format(formatString) + ' UTC';
};

export const ObservationItem = ({
  selected,
  onClick,
  onZoomClick,
  style,
  item,
  seriesInfo,
}) => {
  const [itemType, itemId] = getLayerTypeAndId(item.layer);
  const isMosaic = isMosaicLayer(item.layer);
  const auth = useAuth();

  const thumbnailUrl = useMemo(() => {
    if (isMosaic) {
      return getMosaicThumbnailURL(itemId, auth.user?.accessToken);
    }
    return getImageThumbnailURL(itemType, itemId, auth.user?.accessToken);
  }, [auth.user?.accessToken, isMosaic, itemId, itemType]);

  return (
    <ObservationItemParent style={style} onClick={onClick} selected={selected}>
      <Box
        sx={{
          width: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar selected={selected}>
          <img width="100%" alt={`${itemId} thumbnail`} src={thumbnailUrl} />
        </Avatar>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="subtitle1" noWrap>
          {getFormattedDate(item, isMosaic)}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography sx={{marginRight: 1}}>
            {isMosaic ? seriesInfo.name : item.layer.split(':')[0]}
          </Typography>
          {!isMosaic && (
            <Tooltip title="Zoom to Imagery" placement="bottom">
              <IconButton onClick={onZoomClick}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <CopyButton
            title="Copy Imagery Id"
            tooltipPlacement="bottom"
            color="inherit"
            value={item.layer}
          />
          <ImageryLink layer={item.layer} />
        </Box>
      </Box>
      <Box
        sx={{
          width: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'right',
        }}
      >
        <Box
          sx={{
            paddingRight: 1,
          }}
        >
          <BigChip variant="outlined" label={item.count} />
        </Box>
      </Box>
    </ObservationItemParent>
  );
};

ObservationItem.defaultProps = {
  seriesInfo: {},
};

ObservationItem.propTypes = {
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  onZoomClick: PropTypes.func.isRequired,
  style: PropTypes.object,
  item: PropTypes.object,
  seriesInfo: PropTypes.object,
};
